import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import { colors } from '@/globalStyles';
import { ZOOM } from '@/constants';
import {
  getSelectedSection,
  checkCtrlPressed,
  createClassName,
  isDedicatedMaterial,
  changeContentColor,
} from '@/helpers';
import { handleSectionClick, setRemoveBlurFromAllInputs } from '@/actions';
import SectionRenderer from '../../shared/SectionRenderer';
import { systemStrokeWidthProps } from './styles';

const PageSectionRenderer = props => {
  const {
    page,
    section,
    isMultiColored,
    isSectionSelected,
    moveSection,
    resizeSection,
    onSectionDblClick,
  } = props;
  const scaleCoef = useSelector(state => state.core.scaleCoef);
  const isGridDisplayed = useSelector(state => state.core.isGridDisplayed);
  const gridWidth = useSelector(state => state.core.gridWidth);
  const areSectionsHidden = useSelector(state => state.core.areSectionsHidden);
  const paperColor = useSelector(state => state.core.paperColor);
  const floatableInputVisible = useSelector(
    state => state.core.floatableInputVisible
  );
  const pages = useSelector(state => state.core.pages);
  const sectionsSelection = useSelector(state => state.core.sectionsSelection);
  const dispatch = useDispatch();

  const { x, y, width, height, id, fillColor, ...formatProps } = section;
  const rectProps = { x, y, width, height };
  const { shield: parentShield } = getAllLayers(section.id, {
    [page.uid]: page,
  });
  const corrFillColor =
    paperColor === fillColor ? null : isMultiColored ? fillColor : colors.black;
  const isEditModeEnabled = floatableInputVisible;
  const selectedSection = getSelectedSection({ sectionsSelection, pages });
  const isSectionEdit = isEditModeEnabled && section.id === selectedSection?.id;
  const hideContent = isSectionSelected && isSectionEdit;
  const resizable = isSectionSelected && !areSectionsHidden;
  const clickable = true;
  const draggable = !parentShield?.isMatrix;
  const corrFormatProps = isDedicatedMaterial()
    ? { ...formatProps, ...changeContentColor('#ffffff') }
    : { ...formatProps };

  const onDragEnd = ({ dx, dy }) => {
    let offsetLeft = 0,
      offsetTop = 0;
    if (parentShield && isGridDisplayed && isSectionSelected) {
      // sticky border effect calculation
      const {
        primitiveProps: { x: shieldX, y: shieldY },
      } = parentShield;

      offsetLeft =
        (x + dx / scaleCoef / ZOOM - shieldX).toFixed(2) % gridWidth.toFixed(2);
      offsetTop =
        (y + dy / scaleCoef / ZOOM - shieldY).toFixed(2) % gridWidth.toFixed(2);
      offsetLeft =
        offsetLeft - gridWidth / 2 > 0 && Math.abs(offsetLeft) > 0.01
          ? gridWidth - offsetLeft
          : -offsetLeft;
      offsetTop =
        offsetTop - gridWidth / 2 > 0 && Math.abs(offsetTop) > 0.01
          ? gridWidth - offsetTop
          : -offsetTop;
    }

    moveSection(id, {
      dx: dx / scaleCoef / ZOOM + offsetLeft,
      dy: dy / scaleCoef / ZOOM + offsetTop,
    });
  };

  const onResize = (dx, dy, dw, dh) => {
    let offsetLeft = 0,
      offsetTop = 0,
      offsetRight = 0,
      offsetBottom = 0;
    if (parentShield && isGridDisplayed) {
      // sticky border effect calculation
      const {
        primitiveProps: { x: shieldX, y: shieldY },
      } = parentShield;
      offsetLeft =
        (x + dx / scaleCoef / ZOOM - shieldX).toFixed(2) % gridWidth.toFixed(2);
      offsetTop =
        (y + dy / scaleCoef / ZOOM - shieldY).toFixed(2) % gridWidth.toFixed(2);
      offsetRight =
        (x + width + dw / scaleCoef / ZOOM - shieldX).toFixed(2) %
        gridWidth.toFixed(2);
      offsetBottom =
        (y + height + dh / scaleCoef / ZOOM - shieldY).toFixed(2) %
        gridWidth.toFixed(2);
      offsetLeft =
        offsetLeft - gridWidth / 2 > 0 && Math.abs(offsetLeft) > 0.01
          ? gridWidth - offsetLeft
          : -offsetLeft;
      offsetTop =
        offsetTop - gridWidth / 2 > 0 && Math.abs(offsetTop) > 0.01
          ? gridWidth - offsetTop
          : -offsetTop;
      offsetRight =
        offsetRight - gridWidth / 2 > 0 && Math.abs(offsetRight) > 0.01
          ? gridWidth - offsetRight
          : -offsetRight;
      offsetBottom =
        offsetBottom - gridWidth / 2 > 0 && Math.abs(offsetBottom) > 0.01
          ? gridWidth - offsetBottom
          : -offsetBottom;
    }

    resizeSection(id, {
      dx: dx / scaleCoef / ZOOM + offsetLeft,
      dy: dy / scaleCoef / ZOOM + offsetTop,
      dw: dw / scaleCoef / ZOOM + offsetRight,
      dh: dh / scaleCoef / ZOOM + offsetBottom,
    });
  };

  const handleFastClickTap = event => {
    dispatch(setRemoveBlurFromAllInputs(true));
    if (event.originEvent) {
      if (checkCtrlPressed(event.originEvent)) {
        return dispatch(handleSectionClick({ section, isCtrlPressed: true }));
      }
    }

    if (parentShield.isMatrix) {
      dispatch(handleSectionClick({ section }));
    }
  };

  const handleDoubleClickTap = () => {
    onSectionDblClick(section);
  };

  const className = createClassName({
    clickable,
    draggable: isSectionSelected,
  });

  return (
    <SectionRenderer
      key={`${id}-section-component`}
      id={id}
      className={className}
      svgStyleProps={systemStrokeWidthProps}
      fillColor={corrFillColor}
      resizeType={page.resizeType}
      scaleCoef={scaleCoef}
      rectProps={rectProps}
      formatProps={corrFormatProps}
      section={section}
      shield={parentShield}
      isSectionSelected={isSectionSelected}
      hideSection={areSectionsHidden}
      hideContent={hideContent}
      clickable={clickable}
      skipFirstClickTapOnDbl={isSectionSelected}
      draggable={draggable}
      dragTransforms={page.transforms}
      resizable={resizable}
      onFastClickTap={handleFastClickTap}
      onDblClickTap={handleDoubleClickTap}
      onDragEnd={onDragEnd}
      onResize={onResize}
      groupIdPostfix=""
    />
  );
};

export default React.memo(PageSectionRenderer);
